import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { SelectInputOption } from '@vendasta/galaxy/input';

const I18N_DATE_RANGE_SELECTOR_PREFIX = 'GALAXY.DATE_RANGE.PERIOD_SELECTOR.';

export enum DateRangePresetPeriod {
  custom = 1,
  lastSevenDays = 2,
  lastThirtyDays = 3,
  lastNinetyDays = 4,
  lastSixMonths = 5,
  lastTwelveMonths = 6,
  byMonth = 7,
  byQuarter = 8,
  allTime = 9,
  nextSevenDays = 10,
  nextThirtyDays = 11,
  nextNinetyDays = 12,
  nextSixMonths = 13,
  nextTwelveMonths = 14,
}

export enum BusinessQuarter {
  Q1 = 0,
  Q2 = 1,
  Q3 = 2,
  Q4 = 3,
}

export interface DateRangePeriod {
  id: DateRangePresetPeriod;
  name: string;
  value?: string;
}

const PAST_PERIODS: DateRangePeriod[] = [
  {
    id: DateRangePresetPeriod.lastSevenDays,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}LAST_7_DAYS`,
    value: '7 d',
  },
  {
    id: DateRangePresetPeriod.lastThirtyDays,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}LAST_30_DAYS`,
    value: '30 d',
  },
  {
    id: DateRangePresetPeriod.lastNinetyDays,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}LAST_90_DAYS`,
    value: '90 d',
  },
  {
    id: DateRangePresetPeriod.lastSixMonths,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}LAST_6_MONTHS`,
    value: '6 m',
  },
  {
    id: DateRangePresetPeriod.lastTwelveMonths,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}LAST_12_MONTHS`,
    value: '12 m',
  },
];

const FUTURE_PERIODS: DateRangePeriod[] = [
  {
    id: DateRangePresetPeriod.nextSevenDays,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}NEXT_7_DAYS`,
    value: '7 d',
  },
  {
    id: DateRangePresetPeriod.nextThirtyDays,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}NEXT_30_DAYS`,
    value: '30 d',
  },
  {
    id: DateRangePresetPeriod.nextNinetyDays,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}NEXT_90_DAYS`,
    value: '90 d',
  },
  {
    id: DateRangePresetPeriod.nextSixMonths,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}NEXT_6_MONTHS`,
    value: '6 m',
  },
  {
    id: DateRangePresetPeriod.nextTwelveMonths,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}NEXT_12_MONTHS`,
    value: '12 m',
  },
];

const COMMON_PERIODS: DateRangePeriod[] = [
  {
    id: DateRangePresetPeriod.byMonth,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}BY_MONTH`,
  },
  {
    id: DateRangePresetPeriod.byQuarter,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}BY_QUARTER`,
  },
  {
    id: DateRangePresetPeriod.allTime,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}ALL_TIME`,
  },
  {
    id: DateRangePresetPeriod.custom,
    name: `${I18N_DATE_RANGE_SELECTOR_PREFIX}CUSTOM`,
  },
];

export function getDateRangePeriods(enableFuturePresetButtons = false): DateRangePeriod[] {
  if (enableFuturePresetButtons) {
    return [...FUTURE_PERIODS, ...COMMON_PERIODS];
  }else{
    return [...PAST_PERIODS, ...COMMON_PERIODS];
  }
}

export const DATE_RANGE_PERIODS = getDateRangePeriods();

export interface DateRangeDisplay {
  period: string | null;
  range: string | null;
}

export type CollapseViewportWidth = 'tablet' | 'tablet-large' | 'desktop-small';

// Creates an array of numbers that starts at "j" and increments by one and ends at the value of "k"
export function createNumArray(j: number, k: number): number[] {
  const targetLength = Math.abs(k - j) + 1;
  const a = Array(targetLength);
  const b = [...a];
  const c = b.map((_: number, n: number) => n + j);
  return c;
}

// Function to create year options based on whether future years should be included
export function createYearOptions(allowFutureYearsInDropdown: boolean): SelectInputOption[] {
  const currentYear = new Date().getFullYear();
  const endYear = allowFutureYearsInDropdown ? currentYear + 10 : currentYear;
  return createNumArray(2000, endYear).map((value) => ({
    value,
    label: value.toString(),
    selected: value === currentYear,
  })) as SelectInputOption[];
}

export const monthOptions = createNumArray(0, 11).map((value) => ({
  value,
  label: `GALAXY.DATE_RANGE.MONTHS.MONTH_${value}`,
  selected: value === new Date().getMonth(),
})) as SelectInputOption[];

// Function to get current quarter based on month (0-11)
export function getCurrentQuarter(): BusinessQuarter {
  const currentMonth = new Date().getMonth();
  if (currentMonth >= 0 && currentMonth <= 2) return BusinessQuarter.Q1;   // Jan-Mar
  if (currentMonth >= 3 && currentMonth <= 5) return BusinessQuarter.Q2;   // Apr-Jun
  if (currentMonth >= 6 && currentMonth <= 8) return BusinessQuarter.Q3;   // Jul-Sep
  return BusinessQuarter.Q4;
}

export const quarterOptions = [
  { value: BusinessQuarter.Q1, label: 'Q1', selected: getCurrentQuarter() === BusinessQuarter.Q1 },
  { value: BusinessQuarter.Q2, label: 'Q2', selected: getCurrentQuarter() === BusinessQuarter.Q2 },
  { value: BusinessQuarter.Q3, label: 'Q3', selected: getCurrentQuarter() === BusinessQuarter.Q3 },
  { value: BusinessQuarter.Q4, label: 'Q4', selected: getCurrentQuarter() === BusinessQuarter.Q4 },
] as SelectInputOption[];

export const OVERLAY_POSITIONS = [
  new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
  new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' }),
];
